import { JobReadinessCheckDto, JobReadinessStatus } from '@ups/xplat/api/dto';

/** Presentation for a job-readiness status: FontAwesome icon + theme color class + short label. */
export interface JobReadinessVisual {
  /** FontAwesome 5 icon class (without the `fas` style prefix). */
  icon: string;
  /** Bootstrap/Kendo theme text-color utility (theme-aware, keeps dark mode working). */
  colorClass: string;
  /** Short human label for the state. */
  label: string;
}

const RED: JobReadinessVisual = {
  icon: 'fa-times-circle',
  colorClass: 'text-danger',
  label: 'Not ready',
};
const WARN: JobReadinessVisual = {
  icon: 'fa-exclamation-triangle',
  colorClass: 'text-warning',
  label: 'Review setup',
};
const GREEN: JobReadinessVisual = {
  icon: 'fa-check-circle',
  colorClass: 'text-success',
  label: 'Ready',
};
const NOT_EVALUATED: JobReadinessVisual = {
  icon: 'fa-question-circle',
  colorClass: 'text-muted',
  label: 'Not evaluated',
};

/** Maps a readiness status to its icon/color/label. Unknown values fall back to Not evaluated. */
export function jobReadinessVisual(
  status: JobReadinessStatus
): JobReadinessVisual {
  switch (status) {
    case JobReadinessStatus.Red:
      return RED;
    case JobReadinessStatus.Warn:
      return WARN;
    case JobReadinessStatus.Green:
      return GREEN;
    default:
      return NOT_EVALUATED;
  }
}

const CHECK_LABELS: Record<string, string> = {
  CostTypes: 'Cost Types',
  Crafts: 'Crafts / Billing Template',
  PaySequences: 'Pay Sequences',
  Retention: 'Retention',
};

/** Friendly display name for a check key; falls back to the raw key when unmapped. */
export function jobReadinessCheckLabel(checkKey: string): string {
  return CHECK_LABELS[checkKey] ?? checkKey;
}

/**
 * One-line summary for the icon tooltip. Red means the export/batch-post will be
 * blocked until job setup is fixed (the keyed time itself stays correctable);
 * Warn is non-blocking; Green is clear.
 */
export function jobReadinessTooltip(overall: JobReadinessStatus): string {
  switch (overall) {
    case JobReadinessStatus.Red:
      return 'Job setup incomplete — export/post will be blocked until fixed. Click for details.';
    case JobReadinessStatus.Warn:
      return 'Job setup has a non-blocking warning. Click for details.';
    case JobReadinessStatus.Green:
      return 'Job setup is ready for export. Click for details.';
    default:
      return 'Job readiness unavailable. Click for details.';
  }
}

/** Ordering that puts the most severe checks first in the details dialog. */
export function sortChecksBySeverity(
  checks: JobReadinessCheckDto[]
): JobReadinessCheckDto[] {
  // Copy first — never mutate the caller's array. Red (2) > Warn (1) > Green (0) > NotEvaluated (-1).
  return [...(checks ?? [])].sort((a, b) => b.Status - a.Status);
}
